import React, { Dispatch } from 'react'
import { Button, IconButton, MenuItem, Radio, TextField, Tooltip } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { CREATIVE_IMPORT_LIMITS, SUPPORTED_CALL_TO_ACTIONS } from '@marketing-milk/interfaces'
import { CopyPoolName, CopyPoolsUIState, CreativeImportAction, RowMediaPreview } from '../state'
import { DynamicSelection, clampSelection } from './dynamicSelection'

interface Props {
  rowNumber: number
  pools: CopyPoolsUIState
  previews: RowMediaPreview[]
  disabled: boolean
  /** Which asset/variant the preview is pinned to. A view control — never persisted. */
  selection: DynamicSelection
  onSelectionChange: (next: DynamicSelection) => void
  dispatch: Dispatch<CreativeImportAction>
}

const POOLS: Array<{ name: CopyPoolName; label: string; multiline?: boolean; cta?: boolean }> = [
  { name: 'primaryTexts', label: 'Primary text', multiline: true },
  { name: 'headlines', label: 'Headline' },
  { name: 'descriptions', label: 'Description' },
  { name: 'callToActions', label: 'Call to action', cta: true },
]

/**
 * Editable copy pools for a dynamic row: Facebook mixes one variant from each
 * pool at delivery, so every pool is an ordered list the AE can add to, edit, and
 * trim — not a single field.
 *
 * Every entry also carries a *pin* control that puts it in the preview card.
 * Deliberately a radio and not a star: starring would read as "these are the
 * variants I want to ship", which is a lie — Facebook mixes all of them. It only
 * chooses what you're looking at, so it stays live even on a row that isn't
 * selected for import.
 */
export function CopyPoolEditor({
  rowNumber,
  pools,
  previews,
  disabled,
  selection,
  onSelectionChange,
  dispatch,
}: Props) {
  const setPool = (pool: CopyPoolName, values: string[]) =>
    dispatch({ type: 'editCopyPool', rowNumber, pool, values })

  // Same clamp the preview resolves through, so the highlight can't claim a
  // variant the card isn't showing.
  const pinned = clampSelection(selection, pools, previews.length)

  return (
    <div className="flex flex-col gap-4">
      {/* Which files a dynamic ad uses stays read-only: it comes from the
          manifest, and dropping one here would silently diverge from the archive
          the import is validated against. Clicking only pins an asset into the
          preview — it can't add or remove one. */}
      <div className="flex flex-wrap gap-2">
        {previews.map((preview, index) => {
          const still = preview.kind === 'video' ? preview.posterUrl : preview.sourceUrl
          const isPinned = index === pinned.asset
          return (
            <button
              key={preview.ordinal}
              type="button"
              aria-label={`preview asset ${index + 1}`}
              aria-pressed={isPinned}
              onClick={() => onSelectionChange({ ...pinned, asset: index })}
              className={`h-16 w-16 overflow-hidden rounded border ${
                isPinned ? 'ring-2 ring-blue-500' : 'opacity-70 hover:opacity-100'
              }`}
            >
              {still ? (
                <img src={still} alt="" className="h-full w-full bg-gray-100 object-cover" />
              ) : (
                <div className="h-full w-full bg-gray-200" />
              )}
            </button>
          )
        })}
        <span className="self-center text-xs text-gray-500">
          {previews.length} {previews.length === 1 ? 'asset' : 'assets'} from the manifest
        </span>
      </div>

      {POOLS.map(({ name, label, multiline, cta }) => {
        const values = pools[name]
        const atCap = values.length >= CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants
        return (
          <div key={name} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                {label} · {values.length}/{CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants}
              </span>
              <Tooltip
                title={
                  atCap
                    ? `Facebook allows at most ${CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants} variants per pool.`
                    : ''
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={disabled || atCap}
                    onClick={() =>
                      setPool(name, [...values, cta ? SUPPORTED_CALL_TO_ACTIONS[0] : ''])
                    }
                  >
                    Add
                  </Button>
                </span>
              </Tooltip>
            </div>
            {values.length === 0 && (
              <span className="text-xs text-gray-500">
                No variants — the ad's single {label.toLowerCase()} will be used.
              </span>
            )}
            {values.map((value, index) => (
              <div key={index} className="flex items-start gap-1">
                <Radio
                  size="small"
                  className="mt-1"
                  name={`preview-${name}-${rowNumber}`}
                  checked={index === pinned[name]}
                  // Not gated on `disabled`: that means the row isn't selected for
                  // import, which is no reason to stop looking at what's in it.
                  onChange={() => onSelectionChange({ ...pinned, [name]: index })}
                  inputProps={{ 'aria-label': `preview ${label.toLowerCase()} ${index + 1}` }}
                />
                <TextField
                  select={cta}
                  size="small"
                  fullWidth
                  multiline={multiline}
                  maxRows={4}
                  label={`${label} ${index + 1}`}
                  value={value}
                  disabled={disabled}
                  onChange={e =>
                    setPool(
                      name,
                      values.map((existing, i) => (i === index ? e.target.value : existing))
                    )
                  }
                >
                  {cta &&
                    SUPPORTED_CALL_TO_ACTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {option.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                </TextField>
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label={`remove ${label.toLowerCase()} ${index + 1}`}
                  onClick={() =>
                    setPool(
                      name,
                      values.filter((_, i) => i !== index)
                    )
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}
